import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { staffAPI } from '../services/api';
import { Button, Modal, Badge, Spinner, EmptyState, PageHeader } from '../components/Common';
import { BASE_URL } from '../config';

const emptyForm = {
  name: '', phone: '', email: '', position: '',
  address: '', joining_date: '', salary: '',
};

const POSITIONS = ['Manager', 'Cashier', 'Sales Staff', 'Accountant', 'Delivery', 'Security', 'Cleaner', 'Other'];

const StaffPage = () => {
  const [staff,        setStaff]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [showModal,    setShowModal]    = useState(false);
  const [showAttModal, setShowAttModal] = useState(false);
  const [selStaff,     setSelStaff]     = useState(null);
  const [editId,       setEditId]       = useState(null);
  const [form,         setForm]         = useState(emptyForm);
  const [photoFile,    setPhotoFile]    = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [activeTab,    setActiveTab]    = useState('staff');
  const [attendance,   setAttendance]   = useState([]);
  const [attSummary,   setAttSummary]   = useState({});
  const [todayAtt,     setTodayAtt]     = useState([]);
  const [attMonth,     setAttMonth]     = useState(new Date().getMonth() + 1);
  const [attYear,      setAttYear]      = useState(new Date().getFullYear());

  const load = async () => {
    try {
      const res = await staffAPI.getAll();
      setStaff(res.data.staff);
    } catch { toast.error('Failed to load staff'); }
    finally { setLoading(false); }
  };

  const loadTodayAtt = async () => {
    try {
      const res = await staffAPI.getTodayAttendance();
      setTodayAtt(res.data.staff);
    } catch {}
  };

  useEffect(() => { load(); loadTodayAtt(); }, []);

  const openAdd = () => {
    setForm(emptyForm); setEditId(null);
    setPhotoFile(null); setPhotoPreview(null);
    setShowModal(true);
  };

  const openEdit = (s) => {
    setForm({
      name: s.name, phone: s.phone||'', email: s.email||'',
      position: s.position||'', address: s.address||'',
      joining_date: s.joining_date||'', salary: s.salary||'',
    });
    setEditId(s.id);
    setPhotoPreview(s.photo ? BASE_URL + s.photo : null);
    setPhotoFile(null);
    setShowModal(true);
  };

  const openAttendance = async (s) => {
    setSelStaff(s);
    setShowAttModal(true);
    loadAttendance(s.id, attMonth, attYear);
  };

  const loadAttendance = async (staffId, month, year) => {
    try {
      const res = await staffAPI.getAttendance(staffId, month, year);
      setAttendance(res.data.attendance);
      setAttSummary(res.data.summary);
    } catch {}
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const formData = new FormData();
      Object.keys(form).forEach(key => { if (form[key]) formData.append(key, form[key]); });
      if (photoFile) formData.append('photo', photoFile);

      if (editId) {
        await staffAPI.update(editId, formData);
        toast.success('Staff updated successfully! ✅');
      } else {
        await staffAPI.create(formData);
        toast.success('Staff added successfully! ✅');
      }
      setShowModal(false); load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Something went wrong');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm('"' + name + '" Are you sure you want to delete this?')) return;
    try {
      await staffAPI.delete(id);
      toast.success('"' + name + '" Deleted successfully!');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const handleAttendance = async (staffId, status) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      await staffAPI.markAttendance({ staff_id: staffId, date: today, status });
      toast.success('Attendance marked successfully! ✅');
      loadTodayAtt();
    } catch { toast.error('Something went wrong'); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const statusColor = (status) => {
    if (status === 'present')  return 'green';
    if (status === 'absent')   return 'red';
    if (status === 'half_day') return 'orange';
    if (status === 'leave')    return 'blue';
    return 'gray';
  };

  const statusLabel = (status) => {
    if (status === 'present')    return '✅ Present';
    if (status === 'absent')     return '❌ Absent';
    if (status === 'half_day')   return '🔄 Half Day';
    if (status === 'leave')      return '🏖 Leave';
    if (status === 'not_marked') return '⏳ Mark';
    return status;
  };

  return (
    <div className="page">
      <PageHeader
        title="👨‍💼 Staff Management"
        subtitle={staff.length + ' staff members'}
        actions={<Button onClick={openAdd}>+ Add Staff</Button>}
      />

      {/* Tabs */}
      <div className="tabs">
        <button className={'tab ' + (activeTab==='staff' ? 'active' : '')} onClick={() => setActiveTab('staff')}>
          👥 Staff List ({staff.length})
        </button>
        <button className={'tab ' + (activeTab==='attendance' ? 'active' : '')} onClick={() => { setActiveTab('attendance'); loadTodayAtt(); }}>
          📅 Today's Attendance
        </button>
      </div>

      {/* Staff List Tab */}
      {activeTab === 'staff' && (
        <div className="card">
          <div className="table-wrap">
            {loading ? <Spinner /> : staff.length === 0 ? (
              <EmptyState icon="👨‍💼" title="No staff found." message="'+ Add staff using the 'Add Staff'" />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Photo</th><th>Name</th><th>Position</th>
                    <th>Phone</th><th>Email</th><th>Joining</th>
                    <th>Salary</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {staff.map(s => (
                    <tr key={s.id}>
                      <td>
                        {s.photo ? (
                          <img src={BASE_URL + s.photo} alt={s.name}
                            style={{ width:44, height:44, objectFit:'cover', borderRadius:'50%', border:'2px solid var(--border)' }} />
                        ) : (
                          <div style={{ width:44, height:44, borderRadius:'50%', background:'linear-gradient(135deg, var(--accent), var(--accent2))', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:'#fff', fontSize:16 }}>
                            {s.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </td>
                      <td><strong>{s.name}</strong></td>
                      <td><Badge variant="blue">{s.position || '—'}</Badge></td>
                      <td>{s.phone || '—'}</td>
                      <td style={{ fontSize:12, color:'var(--text2)' }}>{s.email || '—'}</td>
                      <td style={{ fontSize:12, color:'var(--text2)' }}>
                        {s.joining_date ? new Date(s.joining_date).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td style={{ color:'var(--accent2)', fontWeight:600 }}>
                        {s.salary ? 'Rs.' + parseFloat(s.salary).toLocaleString('en-IN') : '—'}
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="btn btn-primary btn-sm" onClick={() => openAttendance(s)}>📅</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => openEdit(s)}>✏️</button>
                          <button className="btn btn-sm" style={{ background:'rgba(255,107,107,.15)', color:'var(--danger)' }} onClick={() => handleDelete(s.id, s.name)}>🗑</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Today Attendance Tab */}
      {activeTab === 'attendance' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">📅 Today's Attendance— {new Date().toLocaleDateString('en-IN', { day:'numeric', month:'long', year:'numeric' })}</span>
          </div>
          <div className="table-wrap">
            {todayAtt.length === 0 ? (
              <EmptyState icon="📅" title="No staff found" message="Please add staff first" />
            ) : (
              <table>
                <thead>
                  <tr><th>Photo</th><th>Name</th><th>Position</th><th>Status</th><th>Mark</th></tr>
                </thead>
                <tbody>
                  {todayAtt.map(s => (
                    <tr key={s.id}>
                      <td>
                        {s.photo ? (
                          <img src={BASE_URL + s.photo} alt={s.name}
                            style={{ width:40, height:40, objectFit:'cover', borderRadius:'50%' }} />
                        ) : (
                          <div style={{ width:40, height:40, borderRadius:'50%', background:'linear-gradient(135deg, var(--accent), var(--accent2))', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:'#fff' }}>
                            {s.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </td>
                      <td><strong>{s.name}</strong></td>
                      <td>{s.position || '—'}</td>
                      <td><Badge variant={statusColor(s.today_status)}>{statusLabel(s.today_status)}</Badge></td>
                      <td>
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="btn btn-success btn-sm" onClick={() => handleAttendance(s.id, 'present')}>✅</button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleAttendance(s.id, 'absent')}>❌</button>
                          <button className="btn btn-warning btn-sm" onClick={() => handleAttendance(s.id, 'half_day')}>🔄</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => handleAttendance(s.id, 'leave')}>🏖</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit Staff Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editId ? '✏️ Staff Edit' : '👤 New Staff Add'} size="md">
        {/* Photo */}
        <div className="form-group">
          <label className="form-label">Photo</label>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <div style={{ width:70, height:70, borderRadius:'50%', background:'var(--bg3)', border:'2px dashed var(--border)', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', flexShrink:0 }}>
              {photoPreview ? (
                <img src={photoPreview} alt="preview" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              ) : <span style={{ fontSize:28 }}>👤</span>}
            </div>
            <label style={{ cursor:'pointer', padding:'8px 14px', background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:8, fontSize:13, color:'var(--text2)' }}>
              📁 Please choose a photo
              <input type="file" accept="image/*" style={{ display:'none' }} onChange={e => {
                const file = e.target.files[0];
                if (file) { setPhotoFile(file); setPhotoPreview(URL.createObjectURL(file)); }
              }} />
            </label>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Name *</label>
            <input className="form-input" placeholder="Staff ka naam" value={form.name} onChange={e => f('name', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Position</label>
            <select className="form-input" value={form.position} onChange={e => f('position', e.target.value)}>
              <option value="">-- Select --</option>
              {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-input" placeholder="10-digit" value={form.phone} onChange={e => f('phone', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" placeholder="email@example.com" value={form.email} onChange={e => f('email', e.target.value)} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Joining Date</label>
            <input className="form-input" type="date" value={form.joining_date} onChange={e => f('joining_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Salary (Rs.)</label>
            <input className="form-input" type="number" placeholder="Monthly salary" value={form.salary} onChange={e => f('salary', e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Address</label>
          <input className="form-input" placeholder="Staff ka address" value={form.address} onChange={e => f('address', e.target.value)} />
        </div>
        <div className="modal-footer">
          <Button variant="ghost" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={handleSave} loading={saving}>💾 {editId ? 'Update' : 'Save'}</Button>
        </div>
      </Modal>

      {/* Attendance History Modal */}
      <Modal isOpen={showAttModal} onClose={() => setShowAttModal(false)} title={'📅 ' + (selStaff?.name || '') + ' — Attendance'} size="md">
        {/* Month/Year selector */}
        <div style={{ display:'flex', gap:10, marginBottom:16 }}>
          <select className="form-input" value={attMonth} onChange={e => {
            setAttMonth(parseInt(e.target.value));
            if (selStaff) loadAttendance(selStaff.id, parseInt(e.target.value), attYear);
          }}>
            {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m,i) => (
              <option key={i+1} value={i+1}>{m}</option>
            ))}
          </select>
          <select className="form-input" value={attYear} onChange={e => {
            setAttYear(parseInt(e.target.value));
            if (selStaff) loadAttendance(selStaff.id, attMonth, parseInt(e.target.value));
          }}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {/* Summary */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:8, marginBottom:16 }}>
          {[
            ['✅ Present', attSummary.present || 0, 'var(--accent2)'],
            ['❌ Absent',  attSummary.absent  || 0, 'var(--danger)'],
            ['🔄 Half',    attSummary.half_day|| 0, 'var(--warn)'],
            ['🏖 Leave',   attSummary.leave   || 0, 'var(--accent)'],
          ].map(([label, count, color]) => (
            <div key={label} style={{ background:'var(--bg3)', borderRadius:8, padding:'10px', textAlign:'center' }}>
              <div style={{ fontSize:11, color:'var(--text3)' }}>{label}</div>
              <div style={{ fontSize:22, fontWeight:700, color }}>{count}</div>
            </div>
          ))}
        </div>

        {/* Attendance list */}
        <div style={{ maxHeight:250, overflowY:'auto' }}>
          {attendance.length === 0 ? (
            <div style={{ textAlign:'center', color:'var(--text3)', padding:20 }}>No attendance records found</div>
          ) : (
            <table>
              <thead><tr><th>Date</th><th>Status</th><th>Check In</th><th>Check Out</th></tr></thead>
              <tbody>
                {attendance.map(a => (
                  <tr key={a.id}>
                    <td style={{ fontSize:12 }}>{new Date(a.date).toLocaleDateString('en-IN')}</td>
                    <td><Badge variant={statusColor(a.status)}>{statusLabel(a.status)}</Badge></td>
                    <td style={{ fontSize:12, color:'var(--text2)' }}>{a.check_in || '—'}</td>
                    <td style={{ fontSize:12, color:'var(--text2)' }}>{a.check_out || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="modal-footer">
          <Button variant="ghost" onClick={() => setShowAttModal(false)}>Close</Button>
        </div>
      </Modal>
    </div>
  );
};

export default StaffPage;